import { Machine, assign } from 'xstate';
import { last, omit } from 'lodash';

import {
  commonErrors as Errors,
  resourceNames,
} from '@zapier/toolbox-redux-framework';

import { postJson } from '@zapier/toolbox-browser-fetch';

import { selectNodes } from 'app/developer-v3/components/PartnerSupportForm/selectors';

type Data = {
  [key: string]: any;
};

type AppSelection = {
  [key: string]: string;
};

type Meta = {
  doc?: string;
  email?: string;
  from_url?: string;
  full_name?: string;
  form?: string;
  form_selection?: string;
  app_selection?: AppSelection;
  logged_in?: boolean;
};

type Node = {
  data: any;
  id: string;
  pathId: string;
  export: () => Data;
};

export type Context = {
  data: Data;
  error: Error | undefined;
  meta: Meta;
  nodes: Node[];
};

export type StateSchema = {
  states: {
    initial: {};
    questions: {};
    confirm: {};
    help: {};
    submitting: {};
    submitted: {};
  };
};

export type States = keyof StateSchema['states'];

type AnswerEvent = { type: 'ANSWER'; pathId: string; data: any };
type UnanswerEvent = { type: 'UNANSWER'; pathId: string };
type SetMetaEvent = {
  type: 'SET_META';
  key: 'email' | 'full_name';
  value: string;
};

export type Event =
  | AnswerEvent
  | UnanswerEvent
  | SetMetaEvent
  | { type: 'CONFIRM' }
  | { type: 'UNCONFIRM' }
  | { type: 'NEXT' }
  | { type: 'SUBMIT' }
  | { type: 'done.invoke.submit' } // onDone
  | { type: 'error.platform.submit'; data: Error } // onError
  | { type: 'CLEAR_ERROR' };

export const unanswer = (data: Data, pathId: string): Data =>
  // Drop both by pathId and nodeId as pre-fill may have used nodeId
  omit(data, [pathId, last(pathId.split('.'))]);

const formMachine = Machine<Context, StateSchema, Event>(
  // Copy paste this next object at https://xstate.js.org/viz/ for a state graph
  {
    id: 'form',
    initial: 'initial',
    states: {
      initial: {
        entry: 'updateNodes',
        on: {
          '': [
            {
              cond: 'needsToAnswerQuestions',
              target: 'questions',
            },
            {
              cond: 'needsToConfirm',
              target: 'confirm',
            },
            {
              target: 'help',
            },
          ],
        },
      },
      questions: {
        on: {
          ANSWER: {
            actions: 'answer',
            target: 'initial',
          },
          UNANSWER: {
            actions: 'unanswer',
            target: 'initial',
          },
        },
      },
      confirm: {
        on: {
          CONFIRM: 'help',
          // some block types use ANSWER to clear only part of their data :P
          ANSWER: {
            actions: 'answer',
            target: 'initial',
          },
          UNANSWER: {
            actions: 'unanswer',
            target: 'initial',
          },
        },
      },
      help: {
        on: {
          UNCONFIRM: [
            {
              cond: 'needsToConfirm',
              target: 'confirm',
            },
            {
              actions: 'unanswerTopic',
              target: 'initial',
            },
          ],
          ANSWER: {
            actions: 'answer',
            target: 'initial',
          },
          SET_META: {
            actions: 'setMeta',
          },
          SUBMIT: {
            target: 'submitting',
          },
          CLEAR_ERROR: {
            actions: 'clearError',
            target: 'help',
          },
        },
      },
      submitting: {
        invoke: {
          id: 'submit',
          data: ({ meta, nodes }) => ({
            meta,
            nodes,
          }),
          src: 'submit',
          onDone: {
            target: 'submitted',
          },
          onError: {
            actions: 'setError',
            target: 'help',
          },
        },
      },
      submitted: {
        on: {
          UNCONFIRM: [
            {
              actions: 'unanswerAll',
              target: 'initial',
            },
          ],
        },
      },
    },
  },
  {
    actions: {
      updateNodes: assign({
        nodes: context => selectNodes(context.meta, context.data),
      }),
      answer: assign({
        data: (context, event: AnswerEvent) => ({
          ...context.data,
          [event.pathId]: event.data,
        }),
      }),
      setMeta: assign({
        meta: (context, event: SetMetaEvent) => ({
          ...context.meta,
          [event.key]: event.value,
        }),
      }),
      unanswer: assign({
        data: (context, event: UnanswerEvent) =>
          unanswer(context.data, event.pathId),
      }),
      unanswerTopic: assign({
        data: context =>
          unanswer(
            context.data,
            context.nodes.find(node => node.id === 'topic').pathId
          ),
      }),
      unanswerAll: assign({
        data: {},
      }),
      setError: assign({
        error: (_context, event: any) => event.data,
      }),
      clearError: assign({
        error: () => undefined,
      }),
    },
    guards: {
      needsToAnswerQuestions: context => last(context.nodes).id !== 'help',
      needsToConfirm: (context, _event, { state: { history } }) =>
        history?.value !== 'help' && // via pre-fill history may not exist
        context.nodes.filter(
          node => !['login', 'topic', 'help'].includes(node.id)
        ).length > 0,
    },
    services: {
      submit: async ({ meta, nodes }) => {
        meta.form = 'partner';
        meta.form_selection = `form-tag-id-${nodes[nodes.length - 2].data}`; // The `form-tag-id-` prefix helps us apply the proper tags in the microservice
        const app_selected = nodes.find(node => node.id === 'app');
        if (app_selected) {
          const app_selection_id = app_selected.data?.id;
          const app_selection_name = app_selected.data?.name;
          const app_selection_intention = app_selected.data?.intention;
          const app_selection_is_public = app_selected.data?.isPublic;
          const app_selection_is_beta = app_selected.data?.isBeta;
          meta.app_selection = {
            app_selection_name,
            app_selection_id,
            app_selection_intention,
            app_selection_is_public,
            app_selection_is_beta,
          };
        }
        const data = {
          meta,
          steps: nodes
            .map(node => node.export())
            .filter(exportedNode => !!exportedNode),
        };

        try {
          await postJson('/api/v4/support/ticket/zendesk/', data);
        } catch (response) {
          throw new Errors.DataPostError({
            resource: resourceNames.supportRequest,
            response,
          });
        }
      },
    },
  }
);

export default formMachine;
